/**
 * Centralized string constants for LexiContest application
 *
 * This file contains all user-facing strings organized hierarchically
 * for type safety, maintainability, and potential internationalization.
 *
 * All strings use TypeScript's 'as const' assertion for literal type inference.
 */

const DISPLAY_NAME_RULES_HINT = 'Letters and numbers only, 2-14 characters';

export const STRINGS = {
  // Application branding and identity
  branding: {
    title: 'LexiContest',
    subtitle: 'The head-to-head word challenge',
    // Hero tagline on the unauthenticated landing masthead (uppercased via CSS).
    tagline: 'The head-to-head word game',
  },

  // Authentication
  auth: {
    buttons: {
      logIn: 'LOG IN',
      createAccount: 'CREATE ACCOUNT',
      loggingIn: 'LOGGING IN',
      creatingAccount: 'CREATING ACCOUNT',
      sendResetLink: 'SEND RESET LINK',
      sendingResetLink: 'SENDING RESET LINK',
      setNewPassword: 'SET NEW PASSWORD',
      settingPassword: 'SETTING PASSWORD',
    },
    labels: {
      email: 'Email',
      password: 'Password',
      displayName: 'Display Name',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
    },
    placeholders: {
      email: 'you@example.com',
      password: 'Password',
      displayName: 'Your name...',
      newPassword: 'New password',
      confirmPassword: 'Confirm new password',
    },
    hints: {
      displayName: DISPLAY_NAME_RULES_HINT,
      email: 'Required. Used to log in and for account recovery.',
      password: 'At least 8 characters',
    },
    links: {
      createAccount: 'Create one',
      noAccount: 'Don’t have an account?',
      haveAccount: 'Already have an account?',
      logIn: 'Log in',
      backToHome: 'Back',
      forgotPassword: 'Forgot password?',
      backToLogin: 'Back to login',
      requestNewLink: 'REQUEST NEW LINK',
    },
    errors: {
      network: 'Could not connect to the server. Please check your connection.',
      unknown: 'Something went wrong. Please try again.',
      passwordTooShort: 'Password must be at least 8 characters',
      emailRequired: 'Email is required',
      emailInvalid: 'Please enter a valid email address',
      passwordsDoNotMatch: 'Passwords do not match',
      invalidCredentials: 'Invalid email or password',
      emailTaken: 'An account with this email already exists',
      nameTaken: 'This name is already taken',
      rateLimited: 'Too many attempts. Please wait.',
      nameInvalidChars: 'Only letters and numbers are allowed',
      nameTooShort: 'Name must be at least 2 characters',
    },
    // Unauthenticated landing conversion copy (the reworked "Landing Concepts" hero).
    landing: {
      howItWorks: '10 letters · 30 seconds · one word wins.',
      liveActivity: 'Real-time, head-to-head play',
      reassurance: 'Free to play · Plays in your browser · No download',
      faceoff: {
        you: 'YOU',
        opponent: 'OPPONENT',
      },
    },
    noAccountNeeded: 'No account needed',
    passwordReset: {
      heading: 'RESET PASSWORD',
      subheading: 'Enter your email and we’ll send a reset link if an account exists.',
      successHeading: 'Check your inbox',
      successExpiry: 'The link expires in 5 minutes.',
      resetFormHeading: 'SET NEW PASSWORD',
      resetSuccessHeading: 'Password updated',
      tokenErrorHeading: 'This link is invalid or has expired',
    },
  },

  // Profile
  profile: {
    title: 'PROFILE',
    stats: {
      totalPoints: 'Total Points',
    },
    buttons: {
      changeName: 'CHANGE DISPLAY NAME',
      deleteAccount: 'DELETE ACCOUNT',
      save: 'SAVE',
      cancel: 'CANCEL',
      confirmDelete: 'DELETE MY ACCOUNT',
    },
    labels: {
      memberSince: 'Member since',
      newName: 'New Display Name',
      dangerZone: 'Danger Zone',
      password: 'Password',
    },
    placeholders: {
      newName: 'Enter new name...',
      password: 'Enter your password to confirm',
    },
    hints: {
      nameRules: DISPLAY_NAME_RULES_HINT,
      deleteWarning: 'This will permanently delete your account after a 30-day grace period.',
    },
    errors: {
      nameTaken: 'This name is already taken',
      wrongPassword: 'Incorrect password',
      invalidName: 'Invalid name',
    },
    success: {
      nameChanged: 'Display name updated successfully',
    },
  },

  // Settings hub
  settingsHub: {
    title: 'Settings',
    close: 'Close settings',
    groups: {
      account: 'Account',
      game: 'Game',
      info: 'Info',
    },
    sections: {
      profile: 'Profile',
      account: 'Account',
      rules: 'Rules',
      feedback: 'Feedback',
      about: 'About',
      privacy: 'Privacy',
      terms: 'Terms',
      cookies: 'Cookies',
    },
    account: {
      title: 'ACCOUNT',
      email: 'Email',
      emailUnverified: 'Unverified',
      password: 'Password',
      passwordMasked: '••••••••',
      changeAction: 'Change',
      changePasswordAction: 'Change password',
      emailLoadError: 'Could not load email',
      emailRetry: 'Retry',
      passwordForm: {
        currentLabel: 'Current password',
        newLabel: 'New password',
        confirmLabel: 'Confirm new password',
        currentPlaceholder: 'Enter current password',
        newPlaceholder: 'Enter new password',
        confirmPlaceholder: 'Re-enter new password',
        hint: 'At least 8 characters.',
        success: 'Password changed successfully',
      },
      errors: {
        wrongPassword: 'Current password is incorrect',
        weakPassword: 'Password must be at least 8 characters',
        mismatch: 'Passwords do not match',
        sessionExpired: 'Your session has expired. Please log in again.',
      },
    },
    cookies: {
      title: 'Cookie Policy',
    },
  },

  // User interface elements
  ui: {
    orientation: {
      heading: 'Rotate your device',
      message:
        'LexiContest is best played in portrait. Please rotate your device upright to continue.',
    },
    buttons: {
      playNow: 'PLAY NOW',
      cancel: 'CANCEL',
      tryAgain: 'TRY AGAIN',
      searchAgain: 'SEARCH AGAIN',
      backToHome: 'BACK TO HOME',
      retryNow: 'RETRY NOW',
      submit: 'SUBMIT',
      submitted: 'SUBMITTED',
      autoSubmitted: 'AUTO-SUBMITTED',
      timeUp: 'TIME UP',
      vowel: 'VOWEL',
      consonant: 'CONSONANT',
      autoSelect: 'PICK FOR ME',
      autoSelectDone: 'LETTERS DEALT',
      autoSelectSeparator: 'or',
      retryConnection: 'RETRY CONNECTION',
      reconnect: 'RECONNECT',
      vsBot: 'SOLO GAME',
      easy: 'EASY',
      medium: 'MEDIUM',
      hard: 'HARD',
      playBotInstead: 'PLAY SOLO',
    },
    input: {
      playerNameLabel: 'Enter Your Name',
      playerNamePlaceholder: 'Your name...',
      wordPlaceholder: 'Type your word...',
      wordHelp: 'Available letters:',
      wordHelpSuffix: 'Maximum 10 letters. Press Enter to submit.',
      wordSubmitted: 'Submitted',
      desktopHint: 'Type on your keyboard · Enter to submit · ⌫ to clear',
    },
    loading: {
      loading: 'Loading',
      loadingEllipsis: 'Loading...',
      connecting: 'Connecting',
    },
    labels: {
      vs: 'VS',
      paused: 'PAUSED',
      waiting: 'Waiting...',
    },
    navigation: {
      skipToMain: 'Skip to main content',
    },
    consent: {
      title: 'We use cookies',
      body: 'We use analytics cookies to understand how you interact with LexiContest and improve your experience. Your session cookie is required for login and is always active.',
      learnMore: 'Learn more',
      accept: 'Accept analytics',
      decline: 'Decline',
    },
    titles: {
      letterSelection: 'LETTER SELECTION',
      letterSelectionGame: 'Letter Selection',
      selectedLetters: 'Selected Letters',
      gameStatus: 'Game Status and Player Information',
      letterSelectionControls: 'Letter Selection Controls',
      connectionStatus: 'Connection Status',
      componentError: 'Failed to Load Component',
    },
    headings: {
      soloGame: 'Solo Game',
      chooseDifficulty: 'Choose your opponent’s difficulty',
    },
    descriptions: {
      easyBot: 'Relaxed pace, shorter words',
      mediumBot: 'Balanced challenge',
      hardBot: 'Aggressive plays, max-length words',
    },
    constraints: {
      maximumVowels: 'Maximum vowels reached',
      maximumConsonants: 'Maximum consonants reached',
      allVowelsSelected: 'All allowed vowels selected',
      allConsonantsSelected: 'All allowed consonants selected',
      vowelDisabled: 'Vowel selection currently disabled',
      consonantDisabled: 'Consonant selection currently disabled',
    },
  },

  // Game results and outcomes
  results: {
    victory: {
      title: 'VICTORY!',
      tie: 'TIE!',
      defeat: 'DEFEAT',
      congratulations: 'Congratulations!',
    },
    words: {
      validWord: 'Valid',
      invalidWord: 'Invalid',
      noWordSubmitted: '\u2014',
    },
    // Why a submitted word was rejected. Each builder returns the reason line in
    // three parts (lead + optional bold letter + optional tail) so the offending
    // letter can be rendered in mono/bold to mirror a tile. See
    // utils/analyze-invalid-word.ts and components/results/WordReason.vue.
    invalidReason: {
      // Composition error: the word uses a letter that isn't in the set at all.
      illegalLetter: (letter: string, moreCount: number) => ({
        lead: 'No ',
        letter,
        tail: moreCount > 0 ? ` and ${moreCount} more not in selection` : ' in selection',
      }),
      // Composition error: the word uses a letter more times than it's available.
      // `available` is pluralized with an apostrophe-s ("2 N's") or kept singular
      // ("1 E"); extra over-used letters append " (+N more)".
      overCount: (letter: string, available: number, moreCount: number) => ({
        lead: `Only ${available} `,
        letter,
        tail:
          (available === 1 ? ' available' : '’s available') +
          (moreCount > 0 ? ` (+${moreCount} more)` : ''),
      }),
      // Word-level error: every letter is legal, the word just isn't real.
      notInWordList: () => ({ lead: 'Not in the word list' }),
      // Fallback: surface the server's own reason verbatim (e.g. "too short").
      fromServer: (reason: string) => ({ lead: reason }),
    },
    actions: {
      playAgain: 'Next Round',
      endGame: 'END GAME',
    },
    // Dense-strip labels for the face-off result layout (no trailing colon /
    // sentence-case, distinct from the title-case section headings elsewhere).
    labels: {
      bestPossible: 'Best possible',
      gameScore: 'Game score',
      selectedLetters: 'Selected letters',
    },
    messages: {
      betterLuckNextTime: 'Better luck next time!',
    },
    matchOver: {
      victory: 'VICTORY!',
      defeat: 'DEFEAT',
      tied: 'TIE GAME!',
      afterRounds: (n: number) => `After ${n} round${n === 1 ? '' : 's'}`,
      roundsWon: (n: number) => `${n} round${n === 1 ? '' : 's'} won`,
      points: (n: number) => `${n} point${n === 1 ? '' : 's'}`,
      lastRound: 'Last Round',
      findNewMatch: 'FIND NEW GAME',
      returnHome: 'RETURN HOME',
    },
    queueFanout: {
      bannerTitle: 'Opponents available',
      bannerSubtitle: 'Start a fresh game with someone new',
      findNewGame: 'FIND NEW GAME',
      raceConditionTitle: 'Someone beat you to it',
      raceConditionSubtitle: 'The available opponent was matched with someone else.',
      continueSolo: 'CONTINUE SOLO',
    },
  },

  // Word-definition icon + modal (#354). The icon sits next to each player's
  // word and the best-possible word; clicking it opens the modal, which fetches
  // GET /api/word/{word} and renders the dictionary entry.
  definitions: {
    title: 'Definition',
    // aria-label for the trigger button beside a word.
    showDefinitionOf: (word: string) => `Show definition of ${word}`,
    close: 'Close definition',
    loading: 'Looking it up...',
    // Surfaced by useWordDefinition on network / server / invalid-body failures.
    error: 'Could not load the definition. Please try again.',
    retry: 'Try again',
    // Coverage gap: the queried word (or a followed parent) has no record.
    emptyTitle: 'No definition found',
    emptyBody: (word: string) => `We don’t have a dictionary entry for “${word}”.`,
    parentEmpty: (word: string) => `No entry for “${word}”.`,
    // Label above a followed parent lemma's entry (e.g. an inflected form's lemma).
    parentLabel: (word: string) => `Definitions · ${word}`,
    // Section + inline labels within a rendered entry.
    pronunciation: 'Pronunciation',
    forms: 'Forms',
    examples: 'Examples',
    formOf: 'Form of',
    altOf: 'Also',
    // Fallback when a part-of-speech block has no `pos` label.
    unknownPos: 'Other',
    // Etymology block (rendered last within an entry). Long text is trimmed to
    // the proximate origin; `etymologyMore`/`etymologyLess` toggle the full text.
    etymology: 'Etymology',
    etymologyMore: 'More',
    etymologyLess: 'Less',
    // Example usages clamp to a few lines when long; these toggle the rest.
    showMoreExamples: 'Show more',
    showFewerExamples: 'Show less',
  },

  // Game status and progress messages
  status: {
    player: {
      you: 'You:',
      youShort: 'You',
      opponent: 'Opponent:',
      submitted: 'Submitted',
      autoSubmittedWord: 'Auto-submitted word \u23f0',
      autoSubmittedEmpty: 'Auto-submitted (no word) \u23f0',
      typing: 'Typing...',
      thinking: 'Thinking...',
      ready: 'Ready',
      offline: 'Offline',
      youLabel: '(You)',
      opponentFallback: 'Opponent',
    },
    game: {
      findingOpponent: 'Finding opponent',
      matchFoundEyebrow: 'Opponent found',
      getReady: 'GET READY',
      waitingForNextRound: 'Waiting for opponent...',
      nextRoundSent: 'Your next round request has been sent',
      returnedToHome: 'Returned to home screen',
    },
    search: {
      matchmakingEyebrow: 'Matchmaking',
      searchingStatus: 'Pairing you with a worthy challenger. This usually takes a few seconds.',
      stillSearching: 'Still searching',
      searchingAnnouncement: 'Searching for opponent',
      searchingDetail: 'Please wait while we find an opponent',
      stillSearchingDetail: 'Taking longer than usual. Hang tight!',
      opponentFoundAnnouncement: 'Opponent found! Game starting soon.',
      noOpponentFound: 'No opponent found. No one else is online right now.',
      // Bot game startup — shown instead of the "Finding Opponent" search copy,
      // since a bot game is never an actual matchmaking search.
      startingBotEyebrow: 'Solo Game',
      startingBotTitle: 'Setting up your game',
      startingBotStatus: 'Dealing you in. This only takes a moment.',
      startingBotAnnouncement: 'Starting solo game',
      startingBotDetail: 'Setting up your solo game',
    },
    connection: {
      connecting: 'Connecting...',
      connectionFailed: 'Connection failed — try again?',
      connectionError: 'Connection error — please try again',
      disconnected: 'Disconnected',
      connectionLostTimer: 'Connection lost — timer may be inaccurate',
      syncConnected: 'Timer synchronized with server',
      syncDisconnected: 'Timer not synchronized with server',
      connectingToServer: 'Connecting to game server...',
      connectedToServer: 'Connected to game server',
      failedToConnect: 'Failed to connect to game server',
      disconnectedFromServer: 'Disconnected from game server',
      connectionLostCheck: 'Connection lost. Please check your internet connection.',
    },
    selection: {
      yourTurn: 'You are selecting the letters',
      opponentSelecting: 'is selecting the letters',
      waitingForAssignment: 'Starting round...',
      selectConsonants: 'Select consonants to balance the letters',
      selectVowels: 'Select vowels to balance the letters',
      selectionComplete: 'Letter selection complete!',
      selectionInProgress: 'Letter selection in progress',
      chooseWisely: 'Choose wisely — each letter shapes the round',
      balanceStrategy: 'Strategy tip: Balance is key',
      selecting: 'SELECTING',
      watching: 'WATCHING',
    },
    wordFinding: {
      findWords: 'Find words using the available letters!',
      everySecond: 'Every second counts! Submit when ready.',
      greatJob: 'Great job! Now let’s see how your opponent does.',
      typeBest: 'Type your best word and hit submit!',
      bothReady: 'Both players ready \u2022 Starting reveal...',
      waitingForOpponent: 'Waiting for opponent to submit...',
    },
  },

  // Error messages
  errors: {
    connection: {
      title: 'Connection Error',
      connectionLost: 'Connection lost',
      reconnectingTitle: 'RECONNECTING',
      reconnectingDetail: 'Lost connection to the server — trying to reconnect...',
      failedToSubmit: 'Failed to submit word — connection lost',
    },
    connectionOverlay: {
      timerWarning: {
        text: 'Game timer is still running',
        icon: 'stopwatch-minimal',
        iconLabel: 'Warning: Game timer still active',
      },
      reconnection: {
        attempting: 'Attempting to reconnect...',
      },
      types: {
        connectionLost: {
          icon: 'warning-inline',
          title: 'Connection Lost',
          description: 'Lost connection to the server.',
          hint: 'Check your network connection and try reconnecting.',
          iconClass: 'error-icon--warning',
          iconLabel: 'Warning: Connection lost to game server',
        },
        serverTimeout: {
          icon: 'alarm-clock',
          title: 'Server Timeout',
          description: 'The game server is taking too long to respond.',
          hint: 'The server might be experiencing high load. Please try again in a moment.',
          iconClass: 'error-icon--timeout',
          iconLabel: 'Warning: Server response timeout',
        },
        syncFailed: {
          icon: 'refresh',
          title: 'Sync Failed',
          description: 'Data may be out of sync.',
          hint: 'Reconnecting will attempt to restore proper game synchronization.',
          iconClass: 'error-icon--sync',
          iconLabel: 'Warning: Game synchronization failed',
        },
        unknown: {
          icon: 'error-x',
          title: 'Connection Error',
          description: 'Please try reconnecting.',
          hint: 'If the problem persists, please check your internet connection.',
          iconClass: 'error-icon--error',
          iconLabel: 'Error: Unknown connection problem',
        },
      },
    },
    timeout: {
      eyebrow: 'No match found',
      title: 'NO ONE’S HERE',
      message: 'Nobody else is online right now — try again in a moment, or play solo.',
    },
    component: {
      defaultMessage: 'There was an error loading this component. Please try again.',
      inputDisabled: 'Input disabled',
      noWebSocket: 'WebSocket connection not available',
      manualReconnectFailed: 'Manual reconnection failed',
      noLetters: 'No letters available',
    },
  },

  // Accessibility labels and descriptions
  aria: {
    labels: {
      roundResults: 'Round results',
      resultActions: 'Result actions',
      playAgainAction: 'Play next round',
      endGameAction: 'End game and view summary',
      wordValidation: 'Word validation status',
      gameMatchmaking: 'Game matchmaking',
      letterSelection: 'Letter selection phase',
      wordFinding: 'Word finding phase',
      errorState: 'Error state',
      availableLetters: 'Available Letters',
      timer: 'Timer',
      wordFormation: 'Word Formation',
      playerStatus: 'Player Status',
      startFindingOpponent: 'Start finding a game opponent',
      cancelSearch: 'Cancel matchmaking search',
      retryConnection: 'Retry connection now',
      tryAgainSearch: 'Try finding opponent again',
      returnHome: 'Return to home screen',
      connectingToGame: 'Connecting to game server',
      matchSummary: 'Game summary',
      matchComplete: 'Game complete',
      matchFound: 'Opponent found, preparing game',
      availableLettersForWord: 'Available letters for word formation',
      successOpponentFound: 'Success: Opponent found',
      alertConnectionLost: 'Alert: Connection lost',
      currentSelector: 'Current selector',
      autoSelectLetters: 'Let the game pick the rest of the letters for you',
      enterWord: 'Enter your word using the available letters',
      wordInput: 'Type your word using available letters',
      wordSubmittedSuccess: 'Word submitted successfully',
      connectionRecovery: 'Connection recovery',
      retryConnectionManually: 'Retry connection manually',
      playerProfile: 'Player',
      wordStatus: 'status:',
      wordLength: 'Word length:',
      turnIndicator: 'Your turn',
      connectionErrorActions: 'Connection error recovery actions',
      clearWord: 'Clear current word',
      deleteLetter: 'Delete letter',
      onscreenKeyboard: 'On-screen keyboard',
      wordSubmittedStatus: 'Word submitted',
      opponentWordSubmitted: 'Opponent word submitted',
      waitingForNextRound: 'Waiting for next round',
      startBotGame: 'Start a solo game',
      selectBotDifficulty: 'Select difficulty level',
      easyBot: 'Play solo on easy difficulty',
      mediumBot: 'Play solo on medium difficulty',
      hardBot: 'Play solo on hard difficulty',
      closeDifficultySelector: 'Close difficulty selector',
      togglePasswordVisibility: 'Toggle password visibility',
      logIn: 'Log in to your account',
      createAccount: 'Create a new account',
      logOut: 'Log out',
      viewProfile: 'View profile',
      backToHome: 'Back to home',
      changeDisplayName: 'Change display name',
      deleteAccount: 'Delete account',
      saveNewName: 'Save new display name',
      cancelNameChange: 'Cancel name change',
      confirmDeleteAccount: 'Confirm account deletion',
      cancelDeleteAccount: 'Cancel account deletion',
      authLoading: 'Checking authentication',
      authentication: 'Authentication',
      loginForm: 'Login form',
      signupForm: 'Signup form',
      forgotPasswordForm: 'Forgot password form',
      resetPasswordForm: 'Reset password form',
      sendFeedback: 'Send feedback',
      feedbackForm: 'Feedback form',
      feedbackType: 'Feedback type',
      closeFeedback: 'Close feedback form',
      legalNavigation: 'Legal',
      openSettings: 'Open settings',
      settingsHub: 'Settings',
      settingsNavigation: 'Settings navigation',
    },
    descriptions: {
      gameIntroductionHeading: 'Game Introduction and Setup',
    },
    navigation: {
      matchmakingActions: 'Matchmaking actions',
      errorRecovery: 'Error recovery actions',
    },
  },

  // Tutorial and rules UX
  tutorial: {
    briefing: {
      title: 'THE BRIEFING ROOM',
      skip: 'SKIP',
      next: 'NEXT',
      done: 'LET’S PLAY',
      stepOf: (n: number, total: number) => `${n} of ${total}`,
      cards: [
        {
          heading: 'Letter Selection',
          bullets: [
            'Each round, one player is the Selector',
            'They pick all 10 letters — at least 3 vowels and 3 consonants',
            'Letters are drawn from weighted pools; the other player watches',
            'The Selector role alternates every round',
          ],
        },
        {
          heading: 'Your Letters',
          bullets: [
            'Once all 10 letters are revealed, both players see the same set',
            'Use any of those letters to build one word',
          ],
        },
        {
          heading: 'Build Your Word',
          bullets: [
            'You have 30 seconds to find your best word',
            'Submit early to lock in your word',
            'If both players submit early, the round ends right away',
          ],
        },
        {
          heading: 'Scoring',
          bullets: [
            'Base: 1 point per letter (3-letter minimum)',
            'Length bonus: +3 (7 letters), +6 (8), +10 (9), +15 (10)',
            'Rare letters J, Q, X, Z: +1 each',
            'Speed: +2 if you submit within 10s, +1 within 20s',
          ],
        },
        {
          heading: 'Winning',
          bullets: [
            'Each round, the higher score wins that round',
            'The game is won by winning the most rounds',
          ],
        },
      ],
    },
    ruleChange: {
      title: 'Rules Updated',
      gotIt: 'GOT IT',
      seeRules: 'SEE FULL RULES',
      items: [
        {
          heading: 'Winning has changed',
          body: 'The game now goes to whoever wins the most rounds, not the most total points. Total points only break a tie when rounds won are equal.',
        },
      ],
    },
    codex: {
      title: 'THE CODEX',
      welcome: 'Welcome, Challenger',
      welcomeBody: 'New to LexiContest? Here’s everything you need to know before your first game.',
      tabs: {
        howItWorks: 'How It Works',
        letters: 'Letters',
        scoring: 'Scoring',
      },
      newBadge: 'New',
      close: 'Close rules',
      howItWorks: [
        {
          heading: 'Letter Selection',
          bullets: [
            'One player selects all 10 letters each round',
            'At least 3 vowels and 3 consonants required, drawn from weighted pools',
            'Selector role alternates every round',
          ],
        },
        {
          heading: 'Build Your Word',
          bullets: [
            'Both players see the same 10 letters',
            '30 seconds to build your best word from them',
            'Submit early to lock in your word; if both submit early, the round ends right away',
          ],
        },
      ],
      letters: [
        {
          heading: 'Common Letters',
          bullets: [
            'Vowels and consonants are drawn from weighted pools',
            'R, S, T, N, L — most frequent consonants',
          ],
        },
        {
          heading: 'Rare Letters',
          bullets: [
            'Q, X, Z, J — uncommon but score extra',
            'Each rare letter earns a +1 bonus on top of base score',
          ],
        },
      ],
      scoring: [
        {
          heading: 'Base Scoring',
          bullets: ['1 point per letter in your word', 'Minimum word length: 3 letters'],
          isRareRule: false,
        },
        {
          heading: 'Bonuses',
          bullets: [
            'Length bonus: +3 (7 letters), +6 (8), +10 (9), +15 (10)',
            '+1 for each rare letter (J, Q, X, Z)',
            'Speed bonus: +2 within 10s, +1 within 20s',
          ],
          isRareRule: true,
        },
        {
          heading: 'Winning',
          bullets: [
            'Each round goes to the higher score that round',
            'The game is won by winning the most rounds',
            'Total points break ties when rounds won are equal',
            'Equal rounds won and total points means the game is a tie',
          ],
          isRareRule: false,
        },
      ],
    },
  },

  // Feedback form
  feedback: {
    trigger: 'Send feedback',
    title: 'Send Feedback',
    prompt: 'Found a bug? Have an idea? We’d love to hear from you.',
    categories: {
      bug: 'Bug',
      idea: 'Idea',
      other: 'Other',
    },
    labels: {
      subject: 'Subject',
      description: 'Description',
      contactOk: 'You may follow up with me via email',
    },
    placeholders: {
      bugSubject: 'Brief summary',
      ideaSubject: 'What’s your idea?',
      otherSubject: 'Brief summary',
      bugDescription: 'What happened? What did you expect?',
      ideaDescription: 'Describe your idea or suggestion',
      otherDescription: 'Tell us more',
    },
    buttons: {
      cancel: 'CANCEL',
      submit: 'SUBMIT',
      sending: 'SENDING...',
      close: 'CLOSE',
    },
    success: {
      title: 'Thanks for your feedback!',
      message: 'Your report has been submitted. We’ll take a look soon.',
    },
    errors: {
      unauthorized: 'Your session has expired. Please log in again.',
      emailNotVerified: 'Please verify your email address before submitting feedback.',
      subjectRequired: 'Subject is required',
      descriptionRequired: 'Description is required',
      rateLimited: 'Too many submissions. Please wait before trying again.',
      featureDisabled: 'Feedback is temporarily unavailable.',
      githubError: 'Could not submit feedback right now. Please try again later.',
      network: 'Could not connect to the server. Please check your connection.',
      unknown: 'Something went wrong. Please try again.',
    },
  },

  // Legal / info pages
  legal: {
    pages: {
      about: {
        title: 'About',
        whatIs: {
          heading: 'What is LexiContest?',
          body: 'LexiContest is a head-to-head word game played in real time. Each round, one player picks the letters, then both players race to find the highest-scoring word. Play against another person or challenge a bot at varying difficulty levels.',
        },
        attributions: {
          heading: 'Attributions',
          body: 'The word list used in LexiContest was built using data extracted from Wiktionary.org. The extraction was performed using wiktextract by Tatu Ylonen, with data sourced from kaikki.org.',
          datasets:
            'The word list and definition datasets LexiContest uses are published under CC BY-SA 4.0:',
        },
        contact: {
          heading: 'Contact',
          body: 'Questions or feedback? Open the settings menu and select Feedback. You\u2019ll need a verified email to submit feedback.',
        },
        copyright: '\u00A9 2025\u20132026 Borb Systems LLC. All rights reserved.',
      },
      privacy: {
        title: 'Privacy Policy',
        lastUpdated: 'Last updated: March 7, 2026',
      },
      terms: {
        title: 'Terms of Use',
        lastUpdated: 'Last updated: March 7, 2026',
      },
      cookies: {
        title: 'Cookie Policy',
        lastUpdated: 'Last updated: March 2026',
        whatAreCookies: {
          heading: 'What Are Cookies?',
          body: 'Cookies are small text files stored on your device by your browser. They help websites remember information about your visit. LexiContest also uses browser local storage, which serves a similar purpose.',
        },
        essentialCookies: {
          heading: 'Essential Cookie',
          body: 'LexiContest uses a single essential cookie to keep you signed in:',
          items: [
            'lexicontest_session — An encrypted session token that authenticates your account. This cookie is HttpOnly (not accessible to JavaScript), uses SameSite protections, and is required for the service to function. It expires when your session ends.',
          ],
        },
        localStorage: {
          heading: 'Local Storage',
          body: 'In addition to cookies, LexiContest stores small amounts of data in your browser’s local storage to maintain your preferences and game state:',
          items: [
            'Game session — Remembers your active game so you can reconnect if your browser refreshes',
            'Tutorial progress — Tracks whether you have completed the tutorial so it is not shown again',
          ],
        },
        analytics: {
          heading: 'Analytics',
          body: 'LexiContest uses OpenPanel, a privacy-focused analytics service, to understand how the game is used. OpenPanel collects anonymized usage data such as page views and general interaction patterns. This data helps us improve the game experience. No personal information is shared with third parties for advertising purposes.',
        },
        managingCookies: {
          heading: 'Managing Cookies',
          body: 'You can control cookies through your browser settings. Most browsers allow you to block or delete cookies. However, blocking the session cookie will prevent you from signing in. Clearing local storage will reset your saved preferences and tutorial progress.',
        },
        contact: {
          heading: 'Contact',
          body: 'For questions about our use of cookies, use the feedback button on the homepage.',
        },
      },
    },
    footer: {
      about: 'About',
      privacy: 'Privacy',
      terms: 'Terms',
    },
    signup: {
      agreement: 'By creating an account, you agree to our',
      termsLink: 'Terms of Use',
      and: 'and',
      privacyLink: 'Privacy Policy',
    },
    guest: {
      agreement: 'By playing, you agree to our',
      termsLink: 'Terms of Use',
      and: 'and',
      privacyLink: 'Privacy Policy',
    },
  },

  // Dynamic content templates
  templates: {
    availableLetter: (letter: string) => `Available letter ${letter}`,
    nameCharacterHelp: (min: number, max: number) =>
      `Enter a name between ${min} and ${max} characters`,
    validationError: (min: number, max: number) => `Name must be ${min}-${max} characters long`,
    startingInSeconds: (seconds: number) =>
      `Starting in ${seconds} second${seconds === 1 ? '' : 's'}...`,
    letterPosition: (position: number, letter?: string) =>
      letter ? `Position ${position}: Letter ${letter}` : `Position ${position}: Empty`,
    letterGridLabel: (count: number, total: number) =>
      `Letter selection row, ${count} of ${total} letters revealed`,
    progressLabel: (current: number, total: number) => `${current} of ${total} letters`,
    progressAriaLabel: (current: number, total: number) =>
      `Letter selection progress: ${current} of ${total} letters selected`,
    timeRemainingAriaLabel: (seconds: number) => `${seconds} seconds remaining`,
    timerAriaLabel: (seconds: number) => `Time remaining: ${seconds} of 30 seconds`,
    vowelAriaLabel: (count: number, enabled: boolean, playerTurn: boolean) => {
      const countText = `${count} of 5 vowels used`;
      const state = enabled && playerTurn ? 'enabled' : 'disabled';
      return `Select vowel - ${countText} - ${state}`;
    },
    consonantAriaLabel: (count: number, enabled: boolean, playerTurn: boolean) => {
      const countText = `${count} of 6 consonants used`;
      const state = enabled && playerTurn ? 'enabled' : 'disabled';
      return `Select consonant - ${countText} - ${state}`;
    },
    vowelConstraint: (count: number) => `${5 - count} vowels remaining`,
    consonantConstraint: (count: number) => `${6 - count} consonants remaining`,
    reconnectAttempt: (attempt: number, max: number) => `Reconnecting... attempt ${attempt}/${max}`,
    submitAriaLabel: (
      seconds: number,
      submitted: boolean,
      enabled: boolean,
      autoSubmitted?: boolean
    ) => {
      if (autoSubmitted) return 'Word was automatically submitted when timer expired';
      if (submitted) return 'Word has been submitted successfully';
      if (!enabled) return 'Submit button is disabled - timer not active or no word entered';
      if (seconds <= 5) return `Submit word immediately - only ${seconds} seconds remaining!`;
      if (seconds <= 10) return `Submit word soon - ${seconds} seconds remaining`;
      return `Submit your word - ${seconds} seconds remaining`;
    },
    autoSubmissionAriaLabel: (submissionType: 'auto-word' | 'auto-empty') => {
      if (submissionType === 'auto-empty') {
        return 'Time expired - no word was entered, so empty submission was automatically sent';
      }
      return 'Time expired - your current word was automatically submitted';
    },
    selectMoreVowels: (count: number) =>
      `Select ${count} more vowel${count > 1 ? 's' : ''} to proceed`,
    selectMoreConsonants: (count: number) =>
      `Select ${count} more consonant${count > 1 ? 's' : ''} to proceed`,
    mustSelectVowels: (count: number) =>
      `You must select at least ${count} more vowel${count > 1 ? 's' : ''} before proceeding.`,
    mustSelectConsonants: (count: number) =>
      `You must select at least ${count} more consonant${count > 1 ? 's' : ''} before proceeding.`,
    progressRemaining: (remaining: number) =>
      `${remaining} more letter${remaining !== 1 ? 's' : ''} needed to complete selection.`,
    vowelProgress: (count: number) => `Vowel selected. ${count} of 5 vowels chosen.`,
    consonantProgress: (count: number) => `Consonant selected. ${count} of 6 consonants chosen.`,
    timerRemaining: (timeRemaining: number) => `(${Math.ceil(timeRemaining)}s remaining)`,
    reconnectionProgress: (attempt: number, maxAttempts: number) => `(${attempt}/${maxAttempts})`,
    reconnectionAriaLabel: (attempt: number, maxAttempts: number) =>
      `Reconnection attempt ${attempt} of ${maxAttempts}`,
    connectionErrorActionsAriaLabel: (errorType: string) =>
      `Connection error recovery actions for ${errorType.replace('_', ' ')}`,
    retryConnectionAriaLabel: (errorType: string) =>
      `Retry connection after ${errorType.replace('_', ' ')} error`,
    resultPlayerScore: (playerName: string, score: number) =>
      `${playerName} scored ${score} points`,
    resultWordSubmission: (playerName: string, word: string, score: number, isValid: boolean) =>
      `${playerName} submitted “${word}” for ${score} points - ${isValid ? 'valid' : 'invalid'} word`,
    resultGameOutcome: (isWinner: boolean, isTie: boolean, opponentName: string) => {
      if (isTie) return 'Round ended in a tie';
      if (isWinner) return `You won against ${opponentName}`;
      return `${opponentName} won the round`;
    },
    resultGameDuration: (durationMs: number) => {
      const seconds = Math.round(durationMs / 1000);
      return `Round lasted ${seconds} seconds`;
    },
    resultCelebration: (isWinner: boolean) =>
      isWinner ? 'Celebrating your victory!' : 'Better luck next time',
    wordLength: (length: number) => `${length} letter${length !== 1 ? 's' : ''}`,
    pointsValue: (points: number) => `${points} point${points !== 1 ? 's' : ''}`,
    roundIndicator: (round: number) => `Round ${round}`,
    totalPoints: (points: number) => `${points} pts`,
    roundsWon: (rounds: number) => `${rounds} rounds won`,
    timerPausedAt: (time: number) => `Timer paused at ${time} seconds`,
    urgentTimeRemaining: (seconds: number) =>
      `URGENT: ${seconds} seconds remaining, time is running short`,
    timeRunningShort: (seconds: number) => `${seconds} seconds remaining, time is running short`,
    opponentSelectingLetter: (name: string) => `${name} is selecting the letters`,
    rateLimitCountdown: (seconds: number) =>
      `Too many attempts. Try again in ${seconds} second${seconds !== 1 ? 's' : ''}.`,
    welcomeToast: (name: string) => `Welcome, ${name}!`,
    memberSince: (date: string) => `Member since ${date}`,
    opponentReadyForRound: (name: string) => `${name} is ready for another round!`,
  },
} as const;

// Export type for TypeScript consumers
export type StringsType = typeof STRINGS;

export default STRINGS;
